import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { ArrowLeft, RefreshCw, ShieldAlert, ShieldCheck, Loader2, AlertTriangle, Users, Activity, KeyRound } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { SEO } from "@/components/SEO";
import { formatDistanceToNow } from "date-fns";

interface HeavyUser { user_id: string; count: number }
interface NegBal { id: string; user_id: string; delta: number; reason: string; balance_after: number; created_at: string }
interface AdminGrant { user_id: string; role: string; created_at: string }
interface SecEvent { id: string; kind: string; user_id: string | null; ip: string | null; details: unknown; created_at: string }
interface LogRow { id?: string; timestamp?: string | number; event_message?: string; status?: number; status_code?: number; method?: string; function_id?: string; msg?: string; path?: string }

interface FeedData {
  ok: true;
  checkedAt: string;
  suspiciousCredits: {
    heavyGranters: HeavyUser[];
    heavyRewardClaimers: HeavyUser[];
    negativeBalances: NegBal[];
  };
  recentAdminGrants: AdminGrant[];
  securityEvents: SecEvent[];
  postgresAlerts: LogRow[];
  authFailures: LogRow[];
  edgeFailures: LogRow[];
}

function StatCard({ icon, label, value, tone = "neutral" }: { icon: React.ReactNode; label: string; value: number | string; tone?: "neutral" | "warn" | "danger" | "ok" }) {
  const tones: Record<string, string> = {
    neutral: "bg-muted/40 text-foreground",
    ok: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20",
    warn: "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20",
    danger: "bg-destructive/10 text-destructive border-destructive/20",
  };
  return (
    <div className={`rounded-xl border p-4 flex items-center gap-3 ${tones[tone]}`}>
      <div className="shrink-0">{icon}</div>
      <div>
        <div className="text-2xl font-semibold leading-none">{value}</div>
        <div className="text-xs opacity-70 mt-1">{label}</div>
      </div>
    </div>
  );
}

export default function AdminSecurity() {
  const isAdmin = useIsAdmin();
  const [data, setData] = useState<FeedData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: res, error: err } = await supabase.functions.invoke("admin-security-feed", { body: {} });
      if (err) throw err;
      if ((res as { ok?: boolean })?.ok) setData(res as FeedData);
      else setError("Could not load security feed");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) load();
  }, [isAdmin]);

  if (isAdmin === null) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!isAdmin) return <Navigate to="/app" replace />;

  const totalAlerts = data
    ? (data.suspiciousCredits.heavyGranters.length +
       data.suspiciousCredits.heavyRewardClaimers.length +
       data.suspiciousCredits.negativeBalances.length +
       data.recentAdminGrants.length +
       data.securityEvents.length +
       data.postgresAlerts.length)
    : 0;

  return (
    <div className="min-h-screen bg-background">
      <SEO title="Security · Admin" description="Intrusion detection and security signals" noindex />

      <header className="sticky top-0 z-10 border-b bg-background/80 backdrop-blur">
        <div className="container max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button asChild variant="ghost" size="icon">
            <Link to="/app/profile" aria-label="Back"><ArrowLeft className="h-4 w-4" /></Link>
          </Button>
          <div className="flex-1">
            <h1 className="font-semibold flex items-center gap-2">
              <ShieldAlert className="h-4 w-4 text-primary" /> Security
            </h1>
            <p className="text-xs text-muted-foreground">
              Intrusion detection · {data ? `updated ${formatDistanceToNow(new Date(data.checkedAt))} ago` : "loading…"}
            </p>
          </div>
          <Button onClick={load} variant="outline" size="sm" disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </header>

      <main className="container max-w-6xl mx-auto px-4 py-6 space-y-6">
        {error && (
          <Card className="border-destructive/40 bg-destructive/5">
            <CardContent className="p-4 text-sm text-destructive">{error}</CardContent>
          </Card>
        )}

        {/* Top KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard
            icon={totalAlerts === 0 ? <ShieldCheck className="h-6 w-6" /> : <ShieldAlert className="h-6 w-6" />}
            label="Total alerts"
            value={data ? totalAlerts : "—"}
            tone={totalAlerts === 0 ? "ok" : totalAlerts > 10 ? "danger" : "warn"}
          />
          <StatCard
            icon={<Users className="h-6 w-6" />}
            label="Failed sign-ins (24h)"
            value={data?.authFailures.length ?? "—"}
            tone={(data?.authFailures.length ?? 0) > 50 ? "warn" : "neutral"}
          />
          <StatCard
            icon={<Activity className="h-6 w-6" />}
            label="Edge errors (1h)"
            value={data?.edgeFailures.length ?? "—"}
            tone={(data?.edgeFailures.length ?? 0) > 20 ? "warn" : "neutral"}
          />
          <StatCard
            icon={<KeyRound className="h-6 w-6" />}
            label="New admins (7d)"
            value={data?.recentAdminGrants.length ?? "—"}
            tone={(data?.recentAdminGrants.length ?? 0) > 0 ? "warn" : "ok"}
          />
        </div>

        {/* Negative balances — impossible without tampering */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-destructive" /> Negative credit balances
            </CardTitle>
            <p className="text-xs text-muted-foreground">A balance below zero is impossible via normal flows — investigate immediately.</p>
          </CardHeader>
          <CardContent>
            {data && data.suspiciousCredits.negativeBalances.length === 0 ? (
              <p className="text-sm text-emerald-600 dark:text-emerald-400 flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> None — clean.</p>
            ) : (
              <ul className="space-y-2">
                {data?.suspiciousCredits.negativeBalances.map((row) => (
                  <li key={row.id} className="text-xs border rounded-lg p-2 bg-destructive/5">
                    <div className="font-mono break-all">{row.user_id}</div>
                    <div className="opacity-80 mt-1">balance {row.balance_after} · {row.reason} · {formatDistanceToNow(new Date(row.created_at))} ago</div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Heavy granters / ad farmers */}
        <div className="grid md:grid-cols-2 gap-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Heavy credit grants (24h)</CardTitle>
              <p className="text-xs text-muted-foreground">Users with &gt;50 positive credit transactions today.</p>
            </CardHeader>
            <CardContent>
              {data && data.suspiciousCredits.heavyGranters.length === 0 ? (
                <p className="text-sm text-muted-foreground">No anomalies.</p>
              ) : (
                <ul className="space-y-1.5">
                  {data?.suspiciousCredits.heavyGranters.map((u) => (
                    <li key={u.user_id} className="flex items-center justify-between text-xs">
                      <span className="font-mono truncate mr-2">{u.user_id}</span>
                      <Badge variant="secondary">{u.count}</Badge>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Heavy rewarded-ad claimers (24h)</CardTitle>
              <p className="text-xs text-muted-foreground">Users at or above the daily ad-grant cap (3).</p>
            </CardHeader>
            <CardContent>
              {data && data.suspiciousCredits.heavyRewardClaimers.length === 0 ? (
                <p className="text-sm text-muted-foreground">No anomalies.</p>
              ) : (
                <ul className="space-y-1.5">
                  {data?.suspiciousCredits.heavyRewardClaimers.map((u) => (
                    <li key={u.user_id} className="flex items-center justify-between text-xs">
                      <span className="font-mono truncate mr-2">{u.user_id}</span>
                      <Badge variant="secondary">{u.count}</Badge>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Recent admin grants */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <KeyRound className="h-4 w-4" /> Admin role grants (7d)
            </CardTitle>
            <p className="text-xs text-muted-foreground">If you didn't authorise one of these, the account may be compromised.</p>
          </CardHeader>
          <CardContent>
            {data && data.recentAdminGrants.length === 0 ? (
              <p className="text-sm text-muted-foreground">No new admins in the last 7 days.</p>
            ) : (
              <ul className="space-y-2">
                {data?.recentAdminGrants.map((g) => (
                  <li key={g.user_id} className="text-xs border rounded-lg p-2 bg-amber-500/5">
                    <div className="font-mono break-all">{g.user_id}</div>
                    <div className="opacity-80 mt-1">granted {formatDistanceToNow(new Date(g.created_at))} ago</div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Postgres / RLS alerts */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Database & RLS alerts (24h)</CardTitle>
            <p className="text-xs text-muted-foreground">RLS denials, tamper-guard exceptions, and Postgres errors.</p>
          </CardHeader>
          <CardContent>
            {data && data.postgresAlerts.length === 0 ? (
              <p className="text-sm text-emerald-600 dark:text-emerald-400 flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> Quiet — no policy violations.</p>
            ) : (
              <ul className="space-y-1.5 max-h-80 overflow-auto">
                {data?.postgresAlerts.slice(0, 30).map((row, i) => (
                  <li key={row.id ?? i} className="text-xs border rounded-lg p-2 font-mono break-all">
                    {String(row.event_message ?? "").slice(0, 240)}
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Auth failures */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Failed auth requests (24h)</CardTitle>
            <p className="text-xs text-muted-foreground">Many failures from one source can indicate credential stuffing.</p>
          </CardHeader>
          <CardContent>
            {data && data.authFailures.length === 0 ? (
              <p className="text-sm text-emerald-600 dark:text-emerald-400 flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> No failed auth requests.</p>
            ) : (
              <ul className="space-y-1.5 max-h-80 overflow-auto">
                {data?.authFailures.slice(0, 50).map((row, i) => (
                  <li key={row.id ?? i} className="text-xs border rounded-lg p-2">
                    <span className="inline-block mr-2 font-semibold">{row.status ?? "?"}</span>
                    <span className="opacity-80">{row.path ?? ""}</span>
                    {row.msg && <div className="opacity-70 mt-1 break-all">{row.msg}</div>}
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Edge function errors */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Edge function errors (1h)</CardTitle>
            <p className="text-xs text-muted-foreground">Hammering of <code>verify-play-purchase</code> or <code>grant-rewarded-credit</code> deserves attention.</p>
          </CardHeader>
          <CardContent>
            {data && data.edgeFailures.length === 0 ? (
              <p className="text-sm text-emerald-600 dark:text-emerald-400 flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> No errors.</p>
            ) : (
              <ul className="space-y-1.5 max-h-80 overflow-auto">
                {data?.edgeFailures.slice(0, 50).map((row, i) => (
                  <li key={row.id ?? i} className="text-xs border rounded-lg p-2 flex items-center justify-between gap-2">
                    <span className="truncate">{row.function_id ?? "—"} · {row.method ?? ""}</span>
                    <Badge variant={Number(row.status_code) >= 500 ? "destructive" : "secondary"}>{row.status_code ?? "?"}</Badge>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Curated security events */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Security events</CardTitle>
            <p className="text-xs text-muted-foreground">Custom alerts written by edge functions. Empty until functions are wired to log here.</p>
          </CardHeader>
          <CardContent>
            {data && data.securityEvents.length === 0 ? (
              <p className="text-sm text-muted-foreground">No events yet.</p>
            ) : (
              <ul className="space-y-2">
                {data?.securityEvents.map((ev) => (
                  <li key={ev.id} className="text-xs border rounded-lg p-2">
                    <div className="flex items-center justify-between">
                      <Badge variant="outline">{ev.kind}</Badge>
                      <span className="opacity-60">{formatDistanceToNow(new Date(ev.created_at))} ago</span>
                    </div>
                    {ev.user_id && <div className="font-mono mt-1 break-all opacity-80">{ev.user_id}</div>}
                    {ev.details ? <pre className="mt-1 opacity-80 whitespace-pre-wrap break-all">{JSON.stringify(ev.details)}</pre> : null}
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
